import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Check, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/layout/use-theme';

interface UpdatingNotificationProps {
  isVisible: boolean;
  message?: string;
  status?: 'updating' | 'success' | 'error';
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  onComplete?: () => void;
}

const UpdatingNotification = ({
  isVisible,
  message = 'Updating database',
  status = 'updating',
  position = 'bottom-right',
  onComplete
}: UpdatingNotificationProps) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4'
  };

  // Status-specific properties
  const statusConfig = {
    updating: {
      icon: RefreshCw,
      gradient: 'from-blue-500 to-indigo-600',
      darkGradient: 'from-blue-400 to-indigo-500',
      bgGradient: 'from-blue-500/10 to-indigo-600/10',
      darkBgGradient: 'from-blue-400/15 to-indigo-500/15',
      borderGradient: 'from-blue-500/30 to-indigo-600/30',
      darkBorderGradient: 'from-blue-400/30 to-indigo-500/30',
      textColor: 'text-blue-600 dark:text-blue-400'
    },
    success: {
      icon: Check,
      gradient: 'from-emerald-500 to-green-600',
      darkGradient: 'from-emerald-400 to-green-500',
      bgGradient: 'from-emerald-500/10 to-green-600/10',
      darkBgGradient: 'from-emerald-400/15 to-green-500/15',
      borderGradient: 'from-emerald-500/30 to-green-600/30',
      darkBorderGradient: 'from-emerald-400/30 to-green-500/30',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    },
    error: {
      icon: Database,
      gradient: 'from-red-500 to-rose-600',
      darkGradient: 'from-red-400 to-rose-500',
      bgGradient: 'from-red-500/10 to-rose-600/10',
      darkBgGradient: 'from-red-400/15 to-rose-500/15',
      borderGradient: 'from-red-500/30 to-rose-600/30',
      darkBorderGradient: 'from-red-400/30 to-rose-500/30',
      textColor: 'text-red-600 dark:text-red-400'
    }
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  // Handle animation complete
  const handleAnimationComplete = () => {
    if (status === 'success' && onComplete) {
      // Wait a bit before calling onComplete to show the success state
      setTimeout(onComplete, 1500);
    }
  };

  return (
    <AnimatePresence onExitComplete={handleAnimationComplete}>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 500, 
            damping: 30,
            mass: 1
          }}
          className={cn(
            'fixed z-50 shadow-lg backdrop-blur-md',
            positionClasses[position],
            'rounded-xl border overflow-hidden',
            `bg-gradient-to-r ${isDark ? config.darkBgGradient : config.bgGradient}`,
            `border-gradient-to-r ${isDark ? config.darkBorderGradient : config.borderGradient}`
          )}
        >
          <div className="flex items-center gap-3 p-3 pr-5">
            {/* Animated icon container */}
            <div className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center',
              `bg-gradient-to-r ${isDark ? config.darkGradient : config.gradient}`,
              'shadow-md'
            )}>
              <motion.div
                animate={status === 'updating' ? { rotate: 360 } : { rotate: 0 }}
                transition={status === 'updating' ? { 
                  repeat: Infinity, 
                  duration: 1.5,
                  ease: "linear"
                } : {}}
                className="text-white"
              >
                <Icon size={18} />
              </motion.div>
            </div>
            
            {/* Message with animated dots */}
            <div className="flex flex-col">
              <div className={cn(
                'font-medium',
                config.textColor
              )}>
                {message}
                {status === 'updating' && (
                  <motion.span
                    animate={{ opacity: [0, 1, 0] }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 1.5,
                      times: [0, 0.5, 1]
                    }}
                  >...</motion.span>
                )}
              </div>
              
              {/* Progress bar for updating status */}
              {status === 'updating' && (
                <motion.div 
                  className="h-1 mt-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ 
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdatingNotification;
